/**
 * ComposeSenderHeader Component
 * Displays sender avatar, name and email
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { TYPOGRAPHY } from '@/design-system';
import { RESPONSIVE } from '@/design-system/helpers/responsive';
import { useLocalizations } from '@/hooks/useLocalizations';
import type { UserData } from './types';

interface ComposeSenderHeaderProps {
  userData: UserData | null | undefined;
  avatarUrl: string | null;
  displayName: string | null;
  compact?: boolean;
}

export function ComposeSenderHeader({ userData, avatarUrl, displayName, compact = false }: ComposeSenderHeaderProps) {
  const { lCommon } = useLocalizations();
  const [avatarError, setAvatarError] = useState(false);

  const senderName = displayName || userData?.display_name || userData?.name;
  const fallbackInitial = (senderName || 'U')[0].toUpperCase();

  const avatarSize = compact ? 'w-8 h-8' : 'w-10 h-10';

  return (
    <div className={cn(RESPONSIVE.flexRowCenterGap3)}>
      <div className={cn(avatarSize, 'rounded-full overflow-hidden bg-muted flex-shrink-0')}>
        {avatarUrl && !avatarError ? (
          <img 
            src={avatarUrl} 
            alt={senderName || lCommon('labels.you', { defaultValue: 'You' })} 
            className="w-full h-full object-cover"
            onError={() => setAvatarError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sm font-bold text-muted-foreground">
            {fallbackInitial}
          </div>
        )}
      </div>
      <div className="flex flex-col min-w-0">
        <span className={cn(compact ? 'text-sm' : TYPOGRAPHY.cardTitleSmall, 'truncate font-medium')}>{senderName}</span>
        <span className={cn(TYPOGRAPHY.caption, 'truncate')}>{userData?.email}</span>
      </div>
    </div>
  );
}
